import React from "react";
import { Helmet } from "react-helmet-async";
import { Users, Award, Home, Heart, Target, Shield } from "lucide-react";
import MeetOurFounder from "../components/MeetOurFounder";
import OurServices from "../components/OurServices";
import { usePage } from "../hooks/useApi";
import { pageApi } from "../services/api";

const AboutPage: React.FC = () => {
  const { data: page, isLoading, error } = usePage("about");

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#FF5A1F] mx-auto mb-4"></div>
          <p className="text-[#334155]">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Error loading page: {error.message}
          </p>
          <p className="text-[#334155]">Using fallback content...</p>
        </div>
      </div>
    );
  }

  // Helper function to get field value with fallback
  const getField = (key: string, fallback: string = ""): string => {
    if (!page) return fallback;
    const fields = pageApi.getPageFields(page);
    return fields[key] || fallback;
  };
  const values = [
    {
      icon: Heart,
      title: getField("value1_title", "Passion"),
      description: getField(
        "value1_description",
        "We are passionate about creating homes that families love and communities that thrive."
      ),
    },
    {
      icon: Target,
      title: getField("value2_title", "Excellence"),
      description: getField(
        "value2_description",
        "We strive for excellence in every aspect of our work, from design to construction to customer service."
      ),
    },
    {
      icon: Shield,
      title: getField("value3_title", "Integrity"),
      description: getField(
        "value3_description",
        "We build trust through honest communication, transparent processes, and reliable delivery."
      ),
    },
    {
      icon: Users,
      title: getField("value4_title", "Community"),
      description: getField(
        "value4_description",
        "We believe in building not just homes, but communities where families can grow and flourish."
      ),
    },
  ];

  const team = [
    {
      name: getField("member1_name", "Michael Thompson"),
      role: getField("member1_role", "Founder & Managing Director"),
      description: getField(
        "member1_bio",
        "With over 20 years in construction, Michael founded Rolleston Homes with a vision to create exceptional homes for New Zealand families."
      ),
      image: getField(
        "member1_image",
        "https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=400"
      ),
    },
    {
      name: getField("member2_name", "Sarah Chen"),
      role: getField("member2_role", "Head of Design"),
      description: getField(
        "member2_bio",
        "Sarah brings 15 years of architectural experience, specializing in sustainable and family-friendly home designs."
      ),
      image: getField(
        "member2_image",
        "https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=400"
      ),
    },
    {
      name: getField("member3_name", "David Wilson"),
      role: getField("member3_role", "Construction Manager"),
      description: getField(
        "member3_bio",
        "David oversees all construction projects, ensuring quality craftsmanship and on-time delivery for every home."
      ),
      image: getField(
        "member3_image",
        "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400"
      ),
    },
  ];

  const milestones = [
    {
      year: getField("milestone1_year", "2008"),
      title: getField("milestone1_title", "Company Founded"),
      description: getField(
        "milestone1_description",
        "Rolleston Homes was established with a commitment to quality and customer satisfaction."
      ),
    },
    {
      year: getField("milestone2_year", "2012"),
      title: getField("milestone2_title", "100th Home Completed"),
      description: getField(
        "milestone2_description",
        "Reached our first major milestone, celebrating 100 families in their new homes."
      ),
    },
    {
      year: getField("milestone3_year", "2016"),
      title: getField("milestone3_title", "Sustainability Focus"),
      description: getField(
        "milestone3_description",
        "Introduced eco-friendly building practices and sustainable design options."
      ),
    },
    {
      year: getField("milestone4_year", "2020"),
      title: getField("milestone4_title", "Award Recognition"),
      description: getField(
        "milestone4_description",
        "Received the Canterbury Builder of the Year award for excellence in construction."
      ),
    },
    {
      year: getField("milestone5_year", "2024"),
      title: getField("milestone5_title", "500+ Services Delivered"),
      description: getField(
        "milestone5_description",
        "Proud to have built over 500 homes across Canterbury, creating lasting communities."
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>
          About Us - Rolleston Homes | Quality Home Builders Canterbury
        </title>
        <meta
          name="description"
          content="Learn about Rolleston Homes - Canterbury's trusted home builders with over 15 years of experience creating quality homes and communities across New Zealand."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
                {getField("hero_title", "About Rolleston Homes")}
              </h1>
              <p className="text-lg text-[#334155] mb-6 leading-relaxed">
                {getField(
                  "hero_content",
                  "Welcome to Rolleston Homes, a trusted and reliable building company based in Rolleston, Christchurch. We are dedicated to providing high-quality construction services to our clients, whether you are looking to build a new home, renovate your existing property, or add an extension to your property. Our team of experienced builders and tradespeople have a wealth of knowledge and expertise in the construction industry, and we pride ourselves on delivering exceptional results that exceed our clients' expectations. We use only the finest materials and latest construction techniques to ensure that every project we undertake is built to last."
                )}
              </p>
            </div>
            <div className="relative">
              <img
                src={getField(
                  "hero_image",
                  "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800"
                )}
                alt="Rolleston Homes construction site"
                className="rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FF5A1F] text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">500+</div>
                <div className="text-sm">Services Delivered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Founder */}
      <MeetOurFounder />

      {/* What We Do / Services */}
      <OurServices />

      {/* Values Section */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("values_title", "Our Values")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "values_subtitle",
                "These core values guide everything we do, from the first consultation to the final handover."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-200"
              >
                <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#FF5A1F]" />
                </div>
                <h3 className="text-xl font-bold text-[#1F2937] mb-4">
                  {value.title}
                </h3>
                <p className="text-[#334155] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("team_title", "Meet Our Team")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "team_subtitle",
                "Our experienced team of professionals is dedicated to bringing your vision to life."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-[#FAFAFA] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                    {member.name}
                  </h3>
                  <div className="text-[#FF5A1F] font-semibold mb-4">
                    {member.role}
                  </div>
                  <p className="text-[#334155] leading-relaxed">
                    {member.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Timeline Section */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("timeline_title", "Our Journey")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "timeline_subtitle",
                "From humble beginnings to becoming Canterbury's trusted home builder."
              )}
            </p>
          </div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-8"
              >
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-[#FF5A1F] text-white rounded-full flex items-center justify-center font-bold">
                    {milestone.year}
                  </div>
                </div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-[#334155] leading-relaxed">
                    {milestone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#FF5A1F] py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-extrabold mb-2">
                {getField("stat1_number", "500+")}
              </div>
              <div className="text-lg">
                {getField("stat1_label", "Homes Built")}
              </div>
            </div>
            <div>
              <div className="text-4xl font-extrabold mb-2">
                {getField("stat2_number", "18+")}
              </div>
              <div className="text-lg">
                {getField("stat2_label", "Years Experience")}
              </div>
            </div>
            <div>
              <div className="text-4xl font-extrabold mb-2">
                {getField("stat3_number", "98%")}
              </div>
              <div className="text-lg">
                {getField("stat3_label", "On-Time Delivery")}
              </div>
            </div>
            <div>
              <div className="text-4xl font-extrabold mb-2">
                {getField("stat4_number", "100%")}
              </div>
              <div className="text-lg">
                {getField("stat4_label", "Customer Satisfaction")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1F2937] py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
            {getField("cta_title", "Ready to Join Our Family?")}
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            {getField(
              "cta_subtitle",
              "Let us help you create the home of your dreams. Contact us today to start your journey with Canterbury's most trusted home builder."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getField("cta_button1_link", "/contact")}
              className="bg-[#FF5A1F] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-[#e4501c] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {getField("cta_button1_text", "Start Your Journey")}
            </a>
            <a
              href={getField("cta_button2_link", "/homes")}
              className="border-2 border-white text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white hover:text-[#1F2937] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {getField("cta_button2_text", "View Our Homes")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
